import { Services } from '@kizi/comparator-core';
import { Div, P } from '@kizi/core-components';
import { IconV2 } from '@kizi/shared-components';
import React from 'react';
import { formatPrice } from '../../utils/global';
import { AvailabilityIcon } from './AvailabilityIcon';
import { ChannelsPackages } from './ChannelsPackages';
import { DisplayGroup } from './groupInterfaces';

export const getDecodersText = ({ decoders }) => {
  if (decoders?.length === 0) {
    return <IconV2 name="small-cancel" size="28px" />;
  }
  return decoders.map(({ rate, name, remarks }) => (
    <Div>
      <Div display="flex" alignItems="center" justifyContent="space-between">
        <P>{name}</P>
        <P>{formatPrice(rate)}</P>
      </Div>

      {remarks && <P>({remarks})</P>}
    </Div>
  ));
};

export const getTelevisionGroup = ({
  televisionInfo,
  networkInformation,
}: Services): DisplayGroup[] => {
  if (!televisionInfo) {
    return [];
  }

  const {
    channels: {
      numberOfTvChannels,
      numberOfHdChannels,
      numberOfRadioChannels,
      channelsPackages,
    },
    decoders,
    numberTvs,
    recordPrograms,
    pauseLiveTv,
    missedPrograms,
    videoOnDemand,
    guide,
  } = televisionInfo;

  const signal = networkInformation?.network.signal;
  const signalList = signal
    ? [
        {
          label: 'Signaaldoorgifte',
          tooltip:
            'Het netwerk dat de aanbieder gebruikt om het digitale tv-signaal door te geven.',
          value: `${signal.toUpperCase()}`,
        },
      ]
    : [];

  const tvList = [
    {
      label: 'TV-zenders',
      tooltip:
        'Het aantal tv-zenders dat standaard is inbegrepen bij dit product.',
      value: numberOfTvChannels,
    },
    {
      label: 'HD-zenders',
      tooltip:
        'Het aantal hd-tv-zenders dat standaard is inbegrepen bij dit product. Hd-tv biedt een veel scherper en kleurrijker beeld dan een analoog signaal.',
      value: numberOfHdChannels,
    },
    {
      label: 'Ontvangers',
      tooltip: 'De tv-ontvangers die je standaard ontvangt bij dit product.',
      value: getDecodersText(decoders),
    },
    {
      label: 'Radiozenders',
      tooltip:
        'Het aantal radiozenders dat standaard is inbegrepen bij dit product.',
      value: numberOfRadioChannels,
    },
    {
      label: 'Zenderpakketten',
      tooltip:
        'Optioneel verkrijgbare zenderpakketten bij deze aanbieder. Optionele zenderpakketten zijn altijd maandelijks opzegbaar.',
      value: (
        <ChannelsPackages channelPackages={channelsPackages.channelPackages} />
      ),
    },
    {
      label: 'Meerdere tv’s aansluiten',
      tooltip: 'Het aantal tv-ontvangers dat je optioneel kan bijbestellen.',
      value: AvailabilityIcon(numberTvs),
    },
    {
      label: 'Opnemen',
      tooltip:
        "Het opnemen en live pauzeren van tv-programma's. Of dit mogelijk is, hangt af van de tv-ontvanger.",
      value: AvailabilityIcon(recordPrograms),
    },
    {
      label: 'Live pauzeren',
      tooltip:
        "Het opnemen en live pauzeren van tv-programma's. Of dit mogelijk is, hangt af van de tv-ontvanger.",
      value: AvailabilityIcon(pauseLiveTv),
    },
    {
      label: 'Programma gemist',
      tooltip:
        "Met Programma Gemist kun je gemiste programma's tot een aantal dagen terugkijken.",
      value: AvailabilityIcon(missedPrograms),
    },
    {
      label: 'Opnameduur',
      tooltip:
        'Het maximaal aantal uur dat er kan worden opgenomen. Verwijder je opnames, dan komt er weer ruimte vrij.',
      value: recordPrograms.recordingTimeInHours,
    },
    {
      label: 'Tegelijkertijd opnemen',
      tooltip: 'Het aantal tv-zenders dat je tegelijkertijd kunt opnemen.',
      value: recordPrograms.numberOfChannels || '',
    },
    {
      label: 'Video on demand',
      tooltip:
        "Te laat ingeschakeld? Met Begin Gemist kun je al begonnen programma's vanaf het begin starten.",
      value: AvailabilityIcon(videoOnDemand),
    },
    {
      label: 'EPG (programmagids)',
      tooltip:
        'Met een elektronische programmagids kun je meer informatie opvragen over de programmering van alle zenders',
      value: AvailabilityIcon(guide),
    },
  ];

  return [...signalList, ...tvList];
};
